import React from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, 
  Clock, 
  TrendingUp, 
  Target,
  BookOpen,
  Award,
  ArrowRight
} from 'lucide-react';

const SkillCard = ({ skill, onLearn, onViewDetails }) => {
  const {
    skill_name = 'Skill Name',
    category = 'Technical',
    difficulty_level = 'beginner',
    estimated_learning_hours = 0,
    priority_score = 0,
    demand_score = 0,
    interest_score = 0,
    salary_impact = 0,
    growth_trend = 'stable',
    learning_resources = [],
    skill_id
  } = skill;

  const getDifficultyColor = (level) => {
    switch (level.toLowerCase()) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend.toLowerCase()) {
      case 'increasing':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'stable':
        return <Target className="h-4 w-4 text-blue-500" />;
      case 'decreasing':
        return <TrendingUp className="h-4 w-4 text-red-500 rotate-180" />;
      default:
        return <Target className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPriorityColor = (score) => {
    if (score >= 0.8) return 'text-red-600 bg-red-50';
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const formatHours = (hours) => {
    if (hours < 1) return '< 1h';
    if (hours < 24) return `${hours}h`;
    const days = Math.ceil(hours / 8);
    return `${days} day${days > 1 ? 's' : ''}`;
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="card hover-lift group cursor-pointer"
      onClick={() => onViewDetails?.(skill)}
    >
      <div className="card-content">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
              {skill_name}
            </h3>
            <p className="text-sm text-gray-600 mt-1">{category}</p>
          </div>
          <div className="flex items-center space-x-1">
            {getTrendIcon(growth_trend)}
            <span className="text-xs text-gray-500 capitalize">{growth_trend}</span>
          </div>
        </div>

        {/* Priority Score */}
        {priority_score > 0 && (
          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(priority_score)} mb-4`}>
            Priority: {Math.round(priority_score * 100)}%
          </div>
        )}

        {/* Skill Details */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-1 text-gray-600">
              <Clock className="h-4 w-4" />
              <span>{formatHours(estimated_learning_hours)}</span>
            </div>
            <span className={`badge ${getDifficultyColor(difficulty_level)}`}>
              {difficulty_level}
            </span>
          </div>

          {/* Scores */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Demand:</span>
              <span className="font-medium">{Math.round(demand_score * 100)}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Interest:</span>
              <span className="font-medium">{Math.round(interest_score * 100)}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Salary Impact:</span>
              <span className="font-medium">{Math.round(salary_impact * 100)}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Growth:</span>
              <span className="font-medium capitalize">{growth_trend}</span>
            </div>
          </div>
        </div>

        {/* Learning Resources */}
        {learning_resources.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-700 mb-2">Learning resources:</p>
            <div className="space-y-1">
              {learning_resources.slice(0, 2).map((resource, index) => (
                <div key={index} className="flex items-center space-x-2 text-xs text-gray-600">
                  <BookOpen className="h-3 w-3" />
                  <span>{resource}</span>
                </div>
              ))}
              {learning_resources.length > 2 && (
                <div className="text-xs text-gray-500">
                  +{learning_resources.length - 2} more resources
                </div>
              )}
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>Learning Progress</span>
            <span>0%</span>
          </div>
          <div className="progress">
            <div className="progress-bar" style={{ width: '0%' }} />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onLearn?.(skill);
            }}
            className="btn btn-primary btn-sm"
          >
            <Brain className="h-4 w-4 mr-2" />
            Start Learning
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails?.(skill);
            }}
            className="btn btn-ghost btn-sm"
          >
            View Details
            <ArrowRight className="h-4 w-4 ml-2" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default SkillCard;
